import { Toaster as Sonner, toast } from 'sonner'

/**
 * What the desk says after something has finished.
 *
 * A toast is for a thing that is **done** and can be missed — "the invitation has
 * been sent again". Anything still true, or anything somebody must act on, is a
 * `StatusBanner` on the screen itself, because a message that disappears is a
 * message somebody looking at their keyboard never saw.
 */
export function Toaster() {
  return (
    <Sonner
      position="bottom-right"
      /**
       * A way to put it away.
       *
       * Without one, a toast that lands over the corner of a table is
       * something to wait out — not long, but long enough to be in the way
       * of the thing you were about to click.
       */
      closeButton
      /**
       * An error stays twice as long.
       *
       * Four seconds is right for "Saved." and short for a sentence somebody
       * has to read and act on: *"That is not your current password"* takes
       * about as long to read as it takes to notice a toast has appeared.
       */
      duration={4_000}
      /**
       * Sonner's own colours, pointed at ours.
       *
       * `classNames` alone was not enough. Sonner sets `--normal-bg` and its
       * siblings on the toast and its own rule wins, so **on a dark desk
       * every toast came up white** — a white card in the corner of a dark
       * screen, which is the thing a bolted-on dark mode always gets wrong.
       * Found by measuring one rather than by looking at it.
       *
       * Variables rather than Sonner's `theme` prop, because the desk's
       * theme is not the browser's: somebody can pin light on a dark
       * machine, and `theme="system"` would then disagree with everything
       * around it.
       */
      style={
        {
          '--normal-bg': 'var(--color-surface)',
          '--normal-text': 'var(--color-ink)',
          '--normal-border': 'var(--color-hairline)',
        } as React.CSSProperties
      }
      toastOptions={{
        classNames: {
          toast: 'rounded-md border border-hairline bg-surface text-ink shadow-e2 text-body',
          description: 'text-ink-2',
          /*
            The tone. Every toast was drawn the same, so a failure and a
            confirmation differed only by Sonner's own glyph.

            Teal is this product's affirmative — there is no `success` token
            and inventing one would be a second green.
          */
          success: '[&_[data-icon]]:text-teal-text',
          error: 'border-error/40 [&_[data-icon]]:text-error',
          actionButton: 'bg-coral-fill text-on-coral rounded-sm',
          closeButton: 'bg-surface border-hairline text-ink-2 hover:text-ink',
        },
      }}
    />
  )
}

export { toast }
